// Acceptance / UAT / defects API surface for the operator SPA.
// Every call goes through the shared request helper — never raw fetch in components.
import { request } from './client.js';
import type { RailCell, RailRoute, RailTone } from '../../shared/rail.js';
import type { Degradable } from '../../shared/problems.js';

// ───────────────────────── types ─────────────────────────

export type DefectSeverity = 'blocking' | 'major' | 'minor';
export type DefectStatus = 'open' | 'in_fix' | 'fixed' | 'verified' | 'closed' | 'withdrawn';
export type TestResult = 'pass' | 'fail' | 'blocked';
export type ArStatus = 'draft' | 'issued' | 'signed' | 'deemed_notice_issued' | 'withdrawn';

export interface SeverityDefinition {
  key: DefectSeverity;
  label: string;
  definition: string;
}
export interface SeverityDefinitions {
  source: string;
  items: SeverityDefinition[];
  body_md?: string;
}

export interface Milestone {
  id: number;
  name: string;
  due_date: string | null;
  amount: string | null;
  status: string;
  published: boolean;
  sort: number;
  deliverable_count: number;
  acceptance_record_count: number;
}

export interface PlanTestCase {
  id: number;
  ref: string;
  title: string;
  status: string;
  published: boolean;
  acceptance_criterion_id: number;
  steps: { n: number; action: string; expected: string }[];
  expected_result: string | null;
  latest_run: { id: number; result: TestResult; note: string | null; run_at: string; run_by: string | null } | null;
}
export interface PlanAc {
  id: number;
  ref: string;
  statement: string;
  measurement_method: string | null;
  status: string;
  published: boolean;
  lint_flags: { pass: boolean; flags: { code: string; severity: string; message: string }[] } | null;
  test_cases: PlanTestCase[];
}
export interface PlanRequirement {
  id: number;
  ref: string;
  type: string;
  priority: string;
  status: string;
  published: boolean;
  statement: string | null;
  acceptance_criteria: PlanAc[];
}
export interface PlanDeliverable {
  id: number;
  ref: string;
  name: string;
  description: string | null;
  status: string;
  published: boolean;
  requirements: PlanRequirement[];
}
export interface UatPlan {
  engagement_id: number;
  milestone: { id: number; name: string; due_date: string | null; status: string; published: boolean } | null;
  publication: Record<string, 'hidden' | 'read' | 'read_act'> | null;
  warnings: { code: string; message: string; fix?: string }[];
  totals: Record<string, number>;
  plan: PlanDeliverable[];
}

export interface TestRun {
  id: number;
  test_case_id: number;
  test_case_ref: string;
  test_case_title: string;
  result: TestResult;
  note: string | null;
  run_at: string;
  has_attachment: boolean;
  run_by: string | null;
  run_by_kind: 'portal' | 'operator';
  portal_role: string | null;
  defects: { id: number; ref: string; severity: DefectSeverity; status: DefectStatus }[];
}

export interface Defect {
  id: number;
  engagement_id: number;
  ref: string;
  severity: DefectSeverity;
  title: string;
  description: string | null;
  status: DefectStatus;
  fix_due_date: string | null;
  created_at: string;
  closed_at: string | null;
  acceptance_criterion_id: number;
  acceptance_criterion_ref: string;
  acceptance_criterion_statement: string;
  requirement_id: number;
  requirement_ref: string;
  requirement_status: string;
  test_case_ref: string | null;
  test_run_id: number | null;
  test_run_result: TestResult | null;
  /**
   * The moment the run that produced this defect was recorded. `DEFECT_SELECT` has selected it
   * since the query was written (`acceptance.ts:429`) and this interface simply never declared
   * it, so the provenance line could not say "from TC-003 run 2026-07-29 14:52". No query change.
   */
  test_run_at: string | null;
  raised_by: string | null;
  raised_by_kind: 'portal' | 'operator';
}

export interface AcceptanceRecord {
  id: number;
  engagement_id: number;
  ref: string;
  milestone_id: number | null;
  milestone_name: string | null;
  status: ArStatus;
  content_hash: string | null;
  content_html?: string;
  issued_at: string | null;
  review_deadline: string | null;
  signed_at: string | null;
  published: boolean;
  created_at: string;
  days_left: number | null;
  window_expired: boolean;
  open_deemed_tasks: number;
  signature: { typed_name: string; signed_at: string; ip_address: string | null; signer: string | null; content_hash: string } | null;
  deliverables: { id: number; ref: string; name: string }[];
  warnings?: string[];
  stats?: Record<string, number>;
}

/**
 * The answer to "may this go, what goes with it, and if not why not" — the wire shape of the
 * server's utils/deletion.ts DeletionPlan. A blocker always names the alternative.
 */
export interface DeletionBlocker {
  code: string;
  message: string;
}
export interface DeletionPlan {
  allowed: boolean;
  blockers: DeletionBlocker[];
  cascade: { what: string; count: number }[];
  label: string;
}

export interface DeemedTask {
  id: number;
  engagement_id: number;
  title: string;
  detail: string;
  status: 'open' | 'done' | 'dismissed';
  created_at: string;
  related: { ar_id: string; ar_ref: string; review_deadline: string; days_overdue: number };
}

// ───────────── the instrument (GET /acceptance/state) ─────────────
//
// Mirrors src/server/routes/acceptanceState.ts. It is a MIRROR and not an import because the
// server module imports `pg`; the shapes it shares with the browser (`RailCell`, `Degradable`)
// come from src/shared/, so the two halves cannot drift on the parts that matter.

export type AcceptanceRailKey = 'REACH' | 'RUN' | 'CLEAR' | 'ISSUED' | 'SIGNED';
export type AcceptanceZone = 'plan' | 'defects' | 'record';

export interface AcceptanceRailCell extends RailCell {
  key: AcceptanceRailKey;
  step: number;
  met: boolean;
  alarm: boolean;
  warn: boolean;
  zone: AcceptanceZone;
}

/** The shared `Move` shape (queueRules.ts) on the wire — emitted by the §2.6 ladder. */
export interface AcceptanceMove {
  rule: string;
  weight: number;
  title: string;
  why: string;
  route: RailRoute;
  refs?: string[];
  tone?: RailTone;
}

export interface ReadinessCondition {
  id: 'portal' | 'matrix' | 'tc_ready' | 'tc_published' | 'ac_published' | 'milestone' | 'lint';
  what: string;
  objects: string;
  done: boolean;
  n: number;
  soft: boolean;
  writes: string;
  action: string;
  route: RailRoute;
  /** B8 — `false` means met only because the universe is empty; it is not a passed check. */
  applicable?: boolean;
  vacuous_reason?: string;
}

export interface CoverageRow {
  id: number;
  ref: string;
  name: string;
  status: string;
  on_records: { ref: string; status: string }[];
}

export interface GateLine {
  gate: string;
  title: string;
  met_count: number;
  total: number;
  conditions: { label: string; met: boolean; detail: string; applicable?: boolean; vacuous_reason?: string }[];
}

export interface DeemedTaskRow {
  id: number;
  engagement_id: number;
  title: string;
  detail: string;
  status: string;
  created_at: string;
  related: Record<string, unknown> | null;
  age_days: number;
}

export interface AcceptanceState extends Degradable {
  engagement: {
    id: number; code: string; name: string; client_name: string;
    review_window_days: number | null; portal_enabled: boolean; currency: string;
  };
  reachability: {
    portal_enabled: boolean;
    portal_users: number;
    matrix: Record<string, string> | null;
    test_cases: { total: number; ready: number; published: number; runnable: number; draft_refs: string[]; unpublished_refs: string[] };
    acceptance_criteria: { total: number; published: number; unpublished_refs: string[]; lint_error_refs: string[] };
    milestones: { total: number; carrying_deliverables: number; unpublished: { id: number; ref: string; name: string }[] };
    conditions: ReadinessCondition[];
    blocked_run: string;
  };
  runs: { recorded: number; pass: number; fail: number; blocked: number; outstanding: number; latest_at: string | null };
  defects: {
    open: number; blocking_open: number; major_open: number; minor_open: number;
    oldest_open_days: number | null;
    worst: { id: number; ref: string; severity: DefectSeverity; status: DefectStatus; fix_due_date: string | null } | null;
  };
  records: {
    total: number; drafts: number; live: number; issued: number; signed: number;
    clock: {
      id: number; ref: string; status: string; issued_at: string | null;
      review_deadline: string | null; days_left: number | null; window_expired: boolean;
    } | null;
    coverage: CoverageRow[];
    uncovered: number;
  };
  tasks: DeemedTaskRow[];
  gates: { G5: GateLine | null; G6: GateLine | null };
  rail: AcceptanceRailCell[];
  position: number;
  ladder_note: string | null;
  moves: AcceptanceMove[];
}

export interface PlanReadyRequest {
  engagement_id: number;
  open_matrix?: boolean;
  mark_ready?: boolean;
  publish_test_cases?: boolean;
  publish_acs?: boolean;
  publish_milestones?: boolean;
}

/** The shared bulk shape (F7.1): nothing is silently skipped, and a refusal names its reason. */
export interface PlanReadyResult {
  engagement_id: number;
  done: { kind: string; refs: string[]; detail: string }[];
  refused: { kind: string; reason: string }[];
  notice: string;
}

export interface OperatorRunResult {
  id: number;
  test_case_id: number;
  test_case_ref: string;
  result: TestResult;
  note: string | null;
  run_at: string;
  run_by_kind: 'operator';
  notice: string;
}

// ───────────────────────── helpers ─────────────────────────

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const patch = (body: unknown): RequestInit => ({ method: 'PATCH', body: JSON.stringify(body) });

// ───────────────────────── api ─────────────────────────

export const acceptanceApi = {
  severityDefinitions: () => request<SeverityDefinitions>('/acceptance/severity-definitions'),

  /**
   * The instrument: the rail, the readiness ledger, the ranked move, the clock, the coverage
   * strip and the two gates, in ONE consistent snapshot. The rungs are relative to each other
   * and must never be assembled from four responses taken at four moments.
   */
  state: (engagementId: number) => request<AcceptanceState>(`/acceptance/state${qs({ engagement_id: engagementId })}`),

  /** N2 — the batched readiness write. The operator confirms a set; the server infers none. */
  planReady: (body: PlanReadyRequest) => request<PlanReadyResult>('/acceptance/plan/ready', json(body)),

  /** N3 — an operator-witnessed run. Always attributed as `operator`, never as the client. */
  recordRun: (body: { test_case_id: number; result: TestResult; note?: string }) =>
    request<OperatorRunResult>('/acceptance/test-runs', json(body)),

  milestones: (engagementId: number) => request<Milestone[]>(`/acceptance/milestones${qs({ engagement_id: engagementId })}`),

  uatPlan: (params: { milestone_id?: number; engagement_id?: number }) =>
    request<UatPlan>(`/acceptance/uat-plan${qs(params)}`),

  testRuns: (engagementId: number, testCaseId?: number) =>
    request<TestRun[]>(`/acceptance/test-runs${qs({ engagement_id: engagementId, test_case_id: testCaseId })}`),

  attachmentUrl: (runId: number) => `/api/acceptance/test-runs/${runId}/attachment`,

  defects: (engagementId: number) =>
    request<{ engagement_id: number; severity: SeverityDefinitions; totals: Record<string, number>; rows: Defect[] }>(
      `/acceptance/defects${qs({ engagement_id: engagementId })}`,
    ),

  createDefect: (body: {
    acceptance_criterion_id: number;
    severity: DefectSeverity;
    title: string;
    description?: string;
    test_run_id?: number;
    fix_due_date?: string;
  }) => request<Defect>('/acceptance/defects', json(body)),

  updateDefect: (
    id: number,
    body: Partial<{ status: DefectStatus; severity: DefectSeverity; fix_due_date: string | null; title: string; description: string }>,
  ) => request<Defect>(`/acceptance/defects/${id}`, patch(body)),

  /** What deleting this defect would take with it. Withdrawing keeps the triage decision. */
  defectDeletionPlan: (id: number) => request<DeletionPlan>(`/acceptance/defects/${id}/deletion-plan`),

  /** Hard delete — for the defect that should never have been raised (a duplicate, a test row). */
  deleteDefect: (id: number) => request<void>(`/acceptance/defects/${id}`, { method: 'DELETE' }),

  convertDefectToCr: (id: number, note?: string) =>
    request<{ defect_ref: string; change_request: { id: number; ref: string; title: string }; notice: string }>(
      `/acceptance/defects/${id}/convert-to-cr`,
      json({ note }),
    ),

  list: (engagementId: number) =>
    request<{ engagement_id: number; portal_visibility: string; rows: AcceptanceRecord[] }>(
      `/acceptance${qs({ engagement_id: engagementId })}`,
    ),

  get: (id: number) => request<AcceptanceRecord>(`/acceptance/${id}`),

  htmlUrl: (id: number) => `/api/acceptance/${id}/html`,

  preview: (body: { engagement_id: number; milestone_id?: number | null; deliverable_ids?: number[]; exclusions_note?: string }) =>
    request<{
      html: string;
      hash: string;
      warnings: string[];
      stats: Record<string, number>;
      deliverable_ids: number[];
      available_deliverables: { id: number; ref: string; name: string; status: string; milestone_id: number | null; milestone_name: string | null }[];
    }>('/acceptance/preview', json(body)),

  create: (body: { engagement_id: number; milestone_id?: number | null; deliverable_ids?: number[]; exclusions_note?: string }) =>
    request<AcceptanceRecord>('/acceptance', json(body)),

  issue: (id: number, reviewDeadline?: string) =>
    request<AcceptanceRecord & { warnings: string[]; notice: string }>(`/acceptance/${id}/issue`, json({ review_deadline: reviewDeadline })),

  deemedNotice: (id: number, note?: string) =>
    request<{
      id: number; ref: string; status: ArStatus; notice_date: string;
      milestone_accepted: string | null; deliverables_accepted: string[]; notice: string;
    }>(`/acceptance/${id}/deemed-notice`, json({ note })),

  withdraw: (id: number) => request<AcceptanceRecord>(`/acceptance/${id}/withdraw`, json({})),

  /** Re-run the composer over the current facts, keeping the ref. Drafts only. */
  refresh: (id: number, body: { exclusions_note?: string; deliverable_ids?: number[] } = {}) =>
    request<AcceptanceRecord & { changed: boolean; notice: string }>(`/acceptance/${id}/refresh`, json(body)),

  /** Discard a draft that was never issued. Anything issued must be withdrawn instead (P4). */
  remove: (id: number) => request<void>(`/acceptance/${id}`, { method: 'DELETE' }),

  deemedTasks: (engagementId?: number) => request<DeemedTask[]>(`/acceptance/tasks/deemed${qs({ engagement_id: engagementId })}`),
};
